// Read a finalized weekly report back out of its Word file (#576 follow-up).
//
// The PM exports the report, fills it in Word, sends it, and uploads the
// finished file to its week. At upload, this reads it twice over:
//
//   • THE WHOLE REPORT, section by section (`filed`): every heading, line,
//     bullet and table, in order, so the Weekly Report tab can show the report
//     as filed and nobody has to open the Word file to read it. Where the file
//     has a photo table, the tab shows the Tracker's own site photos for that
//     day; the images themselves aren't kept.
//   • WHAT WAS TYPED (`carry`), to fill next week's draft:
//       – the customer team table, and the East & Westbrook team rows;
//       – a phone or email typed for someone the job assigns;
//       – Project Details values the Tracker has blank, and rows the PM added;
//       – the three-week look-ahead, by week, so last week's 2nd and 3rd weeks
//         land on this week's 1st and 2nd;
//       – Materials, Survey, Safety and Logistics, tables in them included;
//       – Manpower notes and counts, Equipment lines the PM added;
//       – what the PM typed under a section that is only the Tracker's table
//         (Project Details, the teams, Manpower), a table in the look-ahead,
//         and any section they added, whole, each in its place.
//     The progress summary isn't carried: each week's comes from its own daily
//     logs. ⚠️ Only what DIFFERS from what the Tracker wrote counts as typed
//     (given the report the file was exported from, `context.report`): an
//     untouched export carries nothing, and the Tracker's own data never comes
//     back to override itself next week.
//
// ⚠️ IT FINDS SECTIONS BY THEIR HEADINGS, as the export writes them:
//   • A heading is a paragraph STYLED as one (Heading 1 or 2, or Title), or
//     all-bold CAPITALS (one whose style was cleared to Normal). Only in a
//     file whose heading styles are all gone does any plain line in capitals
//     count, the way the export writes them. A bullet, or an ordinary line that
//     happens to read "Survey" (a daily log's work line, a pasted schedule), is
//     never one: taken for a heading, it would pour what follows into next
//     week's Survey.
//   • A STYLED heading it doesn't know starts a section of the PM's own: shown
//     as filed, and carried whole into next week after the template section it
//     followed. Its text never lands in the section above it. A plain line it
//     doesn't know is just a line. A PM's own sub-heading (Heading 3 and
//     below) stays inside its section.
//   • A "Week of" line it can't place ends that look-ahead week the same way.
//
// What Word does to a file along the way (split runs, bookmarks, fields,
// tracked changes, content controls, merged cells) is unwound below; the specs
// build each case by hand.
//
// In the browser, because the file never reaches the API: it goes straight to
// SharePoint with the PM's own access, like the site photos.

import JSZip from 'jszip';
import type {
  InternalWeeklyReport, WeeklyCarryInput, WeeklyReportBase, WeeklyReportCarryPerson, WeeklyReportExtraSection, WeeklyReportFiled,
  WeeklyReportFiledBlock, WeeklyReportFiledSection, WeeklyReportSectionKey,
} from '@/lib/apiClient';
import {
  addYmd, isJobRole, isTrackerEquipmentLine, jobRoleKeys, labelKey, MANPOWER_FOOTNOTE, manpowerLabel, manpowerNote, TRACKER_LINE_STYLE,
  trackerDetails,
} from './weeklyReportModel';

const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
/** The same vocabulary in a file saved as "Strict Open XML"; read as if it were W. */
const W_STRICT = 'http://purl.oclc.org/ooxml/wordprocessingml/main';

// A no-break space (U+00A0), which Word puts after a heading or between words.
// Built from its code so the source holds no invisible character.
const NBSP = new RegExp(String.fromCharCode(0xa0), 'g');

/**
 * What is kept from one file. Mirrors CARRY_LIMITS in
 * apps/api/src/weekly-report/lib/weekly-uploads.ts, which refuses more: a long
 * section is cut here rather than having the upload refused.
 */
export const CARRY_LIMITS = {
  linesPerSection: 50,
  lineLength: 2000,
  people: 40,
  personField: 300,
  lookaheadWeeks: 3,
  rows: 20,
  tables: 10,
  tableRows: 50,
  tableColumns: 12,
  extraSections: 10,
} as const;

/** What is kept of the whole report as filed. Mirrors FILED_LIMITS in the same API file. */
export const FILED_LIMITS = {
  sections: 40,
  blocksPerSection: 400,
  textLength: 4000,
  titleLength: 200,
  tableRows: 100,
  tableColumns: 12,
  cellLength: 2000,
  totalChars: 400_000,
} as const;

export class NotAWordFileError extends Error {
  constructor() {
    super('That file isn’t a Word document (.docx). Open it in Word and save it as a .docx first.');
    this.name = 'NotAWordFileError';
  }
}

export interface ReadReport {
  /** Which copy the file says it is, from its footer; null when it doesn't say. */
  kind: 'internal' | 'client' | null;
  /** The report week the file's look-ahead starts on, when its headings name one. */
  weekStart: string | null;
  carry: WeeklyCarryInput;
  /** The whole report, section by section, as the tab's "After upload" view shows it. */
  filed: WeeklyReportFiled;
}

/** A team row as the reader needs it: the role, whether the job fills it, and who holds it. */
export interface TeamLike {
  role: string;
  fromJob: boolean;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}

/**
 * What the file was exported from, to tell what the PM TYPED from what the
 * Tracker wrote.
 *
 * `team`: the report's team rows. ⚠️ The team table the file holds is mostly
 * the Tracker's own output: every row for a role the JOB fills (`fromJob`) is
 * its assignment, not anything the PM typed, so all of them are kept out of
 * the rows that carry, by role (isJobRole, which also catches a label the PM
 * added to). Kept, they would make every upload look like it carried
 * something; matched by NAME instead, a row whose person has since been taken
 * off the job would no longer match and would ride along as if typed. Only the
 * template's blank roles, and rows the PM added, carry, plus a phone or email
 * typed for the job's own person (see `jobTeam`).
 *
 * `report`: the week's report as the tab has it, so Project Details, phones,
 * Manpower and (in a file exported before the export marked its own lines)
 * Equipment can be compared with what the export wrote. ⚠️ Without it those
 * carry NOTHING: what was typed there can't be told from the Tracker's own
 * output, and carrying that would bring last week's figures back over this
 * week's. A marked export's Equipment needs no comparison: the mark says.
 *
 * Compared with the report as it is NOW, not as exported: a Project Details
 * value or a Manpower count the Tracker changed between the export and the
 * upload reads as typed. That stays harmless while the Tracker has a value
 * (its own wins); only where it has since gone blank does last week's figure
 * show, marked as from last week's report. Equipment lines don't rely on
 * this: the export marks its own (TRACKER_LINE_STYLE). A marker can't do the
 * same for a count, which the PM types over in place, keeping the mark.
 */
export interface ReadContext {
  team?: readonly TeamLike[];
  report?: WeeklyReportBase & Partial<Pick<InternalWeeklyReport, 'manpower' | 'equipment'>>;
}

type Section = WeeklyReportSectionKey;

type Narrative = 'materials' | 'survey' | 'safety' | 'logistics';
const NARRATIVE: readonly Section[] = ['materials', 'survey', 'safety', 'logistics'];
/** Template sections that are only the Tracker's table (Equipment's lines carry on their own). */
const TABLE_SECTIONS: readonly Section[] = ['details', 'customerTeam', 'ewTeam', 'manpower', 'equipment'];

/** Headings by their letters alone, so "Three Week Look-Ahead" and "THREE-WEEK LOOK AHEAD" match. */
const HEADINGS: Record<string, Section> = {
  PROJECTDETAILS: 'details',
  'EAST&WESTBROOKCONSTRUCTIONTEAM': 'ewTeam',
  EASTANDWESTBROOKCONSTRUCTIONTEAM: 'ewTeam',
  WEEKLYPROGRESSSUMMARY: 'progress',
  THREEWEEKLOOKAHEAD: 'lookahead',
  '3WEEKLOOKAHEAD': 'lookahead',
  MATERIALS: 'materials',
  MANPOWER: 'manpower',
  SURVEY: 'survey',
  SAFETY: 'safety',
  EQUIPMENT: 'equipment',
  LOGISTICS: 'logistics',
};

const headingKey = (text: string) => text.toUpperCase().replace(/[^A-Z0-9&]/g, '');

type Block =
  /**
   * `list`: a bullet or numbered item (never a heading-styled paragraph, which
   * Word can number too). `level`: its heading level, 1 for Title; 0 when it
   * isn't styled as a heading. `bold`: every run with text is bold, as the
   * export writes its headings (and as they stay when the style is cleared to Normal).
   */
  | { kind: 'p'; text: string; list: boolean; level: number; bold: boolean; tracker: boolean }
  /** `header`: its first row repeats as a header (as the export's tables do). `photos`: it holds pictures. */
  | { kind: 'tbl'; rows: string[][]; header: boolean; photos: boolean; lines: { own: number; others: string[] } };

const wChildren = (el: Element, name: string) =>
  Array.from(el.children).filter((c) => c.namespaceURI === W && c.localName === name);

/** The W children of `el`, looking through content controls and custom XML wrapped around them. */
function unwrapped(el: Element, name: string): Element[] {
  const out: Element[] = [];
  for (const c of Array.from(el.children)) {
    if (c.namespaceURI !== W) continue;
    if (c.localName === name) out.push(c);
    else if (c.localName === 'sdt' && !isPlaceholder(c)) wChildren(c, 'sdtContent').forEach((s) => out.push(...unwrapped(s, name)));
    else if (c.localName === 'customXml') out.push(...unwrapped(c, name));
  }
  return out;
}

/** A content control still showing its prompt ("Click or tap here to enter text."): nothing anyone typed. */
function isPlaceholder(sdt: Element): boolean {
  const pr = wChildren(sdt, 'sdtPr')[0];
  return !!pr && wChildren(pr, 'showingPlcHdr').length > 0;
}

/** A boolean property element (w:b, w:tblHeader) that is on: present, and not val="false"/"0". */
const isOn = (el: Element | undefined) => {
  if (!el) return false;
  const val = el.getAttributeNS(W, 'val');
  return val !== 'false' && val !== '0';
};

/** A paragraph's style id, '' for none. */
function paragraphStyle(p: Element): string {
  const pPr = wChildren(p, 'pPr')[0];
  return (pPr && wChildren(pPr, 'pStyle')[0]?.getAttributeNS(W, 'val')) ?? '';
}

const isTrackerStyle = (style: string) => style.toLowerCase() === TRACKER_LINE_STYLE.toLowerCase();

/**
 * What a paragraph's properties say it is: a list item, a heading of some
 * level, or neither; whether it is all bold; and whether the export marked it
 * as its own line (TRACKER_LINE_STYLE).
 */
function paragraphKind(p: Element): { list: boolean; level: number; bold: boolean; tracker: boolean } {
  const pPr = wChildren(p, 'pPr')[0];
  const style = paragraphStyle(p);
  const level = /^Title$/i.test(style) ? 1 : Number(/^Heading\s?(\d)$/i.exec(style)?.[1] ?? 0);
  const numPr = pPr ? wChildren(pPr, 'numPr')[0] : undefined;
  // numId 0 is numbering switched OFF, not a list.
  const numbered = !!numPr && wChildren(numPr, 'numId')[0]?.getAttributeNS(W, 'val') !== '0';
  const runs = Array.from(p.getElementsByTagNameNS(W, 'r'))
    .filter((r) => wChildren(r, 't').some((t) => (t.textContent ?? '').trim()));
  const bold = runs.length > 0 && runs.every((r) => {
    const rPr = wChildren(r, 'rPr')[0];
    return isOn(rPr ? wChildren(rPr, 'b')[0] : undefined);
  });
  // A numbered HEADING is still a heading: Word's Numbering button puts the
  // same numPr on a Heading 2 as on a bullet.
  // `tracker`: a line the export wrote from the job's own data (TRACKER_LINE_STYLE).
  return { list: !level && (numbered || /^List/i.test(style)), level, bold, tracker: isTrackerStyle(style) };
}

/**
 * A paragraph's text as it reads: runs joined, tabs and line breaks kept.
 * Left out: deleted and moved-away text (a tracked move keeps the text at both
 * ends of the XML), field codes, text boxes (paragraphs of their own),
 * markup-compatibility fallbacks, and content controls still showing their
 * prompt.
 */
function paragraphText(p: Element): string {
  let out = '';
  const walk = (el: Element) => {
    for (const c of Array.from(el.children)) {
      if (c.localName === 'Fallback') continue;
      if (c.namespaceURI !== W) { walk(c); continue; }
      switch (c.localName) {
        case 't': out += c.textContent ?? ''; break;
        case 'tab': out += '\t'; break;
        // Any break, a page or column break too: the words either side are separate.
        case 'cr': case 'br': out += '\n'; break;
        case 'noBreakHyphen': out += '-'; break;
        case 'sdt': if (!isPlaceholder(c)) walk(c); break;
        // Properties (a tab STOP is a w:tab inside w:pPr), deleted or moved-away
        // text, field codes, text boxes, a soft hyphen (shown only at a line end).
        case 'pPr': case 'rPr': case 'del': case 'delText': case 'moveFrom': case 'instrText':
        case 'txbxContent': case 'softHyphen':
          break;
        default: walk(c);
      }
    }
  };
  walk(p);
  return out;
}

/** A cell's text: its paragraphs, including those inside content controls; a table nested in it is skipped. */
function cellText(tc: Element): string {
  return unwrapped(tc, 'p').map(paragraphText).join('\n').replace(NBSP, ' ').trim();
}

/**
 * A table's rows as grid columns. A cell spanning N columns is followed by
 * N-1 empty ones, so later columns stay under their headers; a vertical-merge
 * continuation takes the text of the cell above it (two Foreman rows sharing
 * one merged "Foreman" cell are both Foreman).
 */
function tableRows(tbl: Element): string[][] {
  const rows: string[][] = [];
  for (const tr of unwrapped(tbl, 'tr')) {
    const cells: string[] = [];
    // Grid columns a row skips before its first cell ("gridBefore"), so its cells stay under their headers.
    const trPr = wChildren(tr, 'trPr')[0];
    const before = Math.max(0, Number(trPr && wChildren(trPr, 'gridBefore')[0]?.getAttributeNS(W, 'val')) || 0);
    for (let k = 0; k < before; k++) cells.push('');
    for (const tc of unwrapped(tr, 'tc')) {
      const tcPr = wChildren(tc, 'tcPr')[0];
      const span = Math.max(1, Number(tcPr && wChildren(tcPr, 'gridSpan')[0]?.getAttributeNS(W, 'val')) || 1);
      const vMerge = tcPr && wChildren(tcPr, 'vMerge')[0];
      const continues = !!vMerge && vMerge.getAttributeNS(W, 'val') !== 'restart';
      const col = cells.length;
      cells.push(continues ? (rows[rows.length - 1]?.[col] ?? '') : cellText(tc));
      for (let k = 1; k < span; k++) cells.push('');
    }
    rows.push(cells);
  }
  return rows;
}

/** Whether the table's first row is marked to repeat as a header, as the export marks its tables'. */
function headerRow(tbl: Element): boolean {
  const first = unwrapped(tbl, 'tr')[0];
  const trPr = first && wChildren(first, 'trPr')[0];
  return isOn(trPr ? wChildren(trPr, 'tblHeader')[0] : undefined);
}

/**
 * A table's lines of text, split into the export's own (marked with
 * TRACKER_LINE_STYLE, as its photo grid's captions and "Photo not available"
 * are) and everyone else's, in order.
 */
function tableLines(tbl: Element): { own: number; others: string[] } {
  let own = 0;
  const others: string[] = [];
  for (const p of Array.from(tbl.getElementsByTagNameNS(W, 'p'))) {
    const text = paragraphText(p).replace(NBSP, ' ').trim();
    if (!text) continue;
    if (isTrackerStyle(paragraphStyle(p))) own++; else others.push(text);
  }
  return { own, others };
}

/** A table holding pictures: the export's photo grid, or one the PM made. */
const holdsPictures = (tbl: Element) =>
  tbl.getElementsByTagNameNS(W, 'drawing').length > 0 || tbl.getElementsByTagNameNS(W, 'pict').length > 0;

/** The body's paragraphs and tables in reading order, looking inside content controls. */
function bodyBlocks(body: Element): Block[] {
  const out: Block[] = [];
  const visit = (el: Element) => {
    for (const c of Array.from(el.children)) {
      if (c.namespaceURI !== W) continue;
      if (c.localName === 'p') out.push({ kind: 'p', text: paragraphText(c), ...paragraphKind(c) });
      else if (c.localName === 'tbl') out.push({ kind: 'tbl', rows: tableRows(c), header: headerRow(c), photos: holdsPictures(c), lines: tableLines(c) });
      else if (c.localName === 'sdt') { if (!isPlaceholder(c)) wChildren(c, 'sdtContent').forEach(visit); }
      else if (c.localName === 'customXml') visit(c);
    }
  };
  visit(body);
  return out;
}

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** "Week of" at the start of a line, an optional colon after it. */
const WEEK_OF = /^week\s+of\b\s*:?\s*(.*)$/i;
/** A weekday before the date: "Week of Monday, October 12", "Week of Mon 10/12". */
const WEEKDAY = /^(?:sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)(?:[a-z]*)\.?,?\s+/i;
/**
 * The far end of a week label's range: a DATE or day ("Oct 10", "10/10",
 * "2026-10-10", "Saturday, October 17, 2026", "10"), never words, so "Week of
 * 10/18 - crane arrives" stays a line about that week.
 */
const RANGE_END = String.raw`(?:(?:sun|mon|tue|wed|thu|fri|sat)[a-z]*\.?,?\s*)?`
  + String.raw`(?:(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s*)?`
  + String.raw`\d{1,4}(?:st|nd|rd|th)?(?:[/.-]\d{1,4}){0,2}(?:,?\s*\d{4})?`;
/**
 * What may follow a week label's date and still leave it a LABEL rather than a
 * line that merely starts with one ("Week of 10/18 crane arrives: set pad
 * now"): nothing, a colon or period, a range to another date ("- Oct 10",
 * "thru 10/10"; "to" and "thru" as whole words, so "top out" is not "to p
 * out"), a note in parentheses, or both.
 */
const LABEL_TAIL = new RegExp(
  String.raw`^\s*[:.]?\s*(?:(?:[-–—]|\b(?:thru|through|to|until)\b)\s*${RANGE_END})?\s*(?:\([^)]*\))?\s*[:.]?\s*$`, 'i');

const dayNumber = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d) / 86_400_000;
const ymd = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const fullYear = (s: string | undefined) => (s ? Number(s.length === 2 ? `20${s}` : s) : null);
const monthOf = (word: string) => {
  const w = word.toLowerCase();
  return MONTHS.findIndex((name) => name === w || (w.length >= 3 && name.startsWith(w))) + 1;
};

/** The date at the start of `s` (after "Week of"), and what follows it; null when there is none. */
function leadingDate(s: string): { month: number; day: number; year: number | null; tail: string } | null {
  const rest = s.replace(WEEKDAY, '');
  let m: RegExpExecArray | null;
  // 2026-10-04
  if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(rest))) {
    return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]), tail: rest.slice(m[0].length) };
  }
  // 10/4, 10-4-26, 10.4.2026 (month first, as written here). The year must
  // follow the SAME separator: in "10/4-10/10" the "-10" starts a range, not a year.
  if ((m = /^(\d{1,2})([/.-])(\d{1,2})(?:\2(\d{4}|\d{2}))?\b/.exec(rest))) {
    return { month: Number(m[1]), day: Number(m[3]), year: fullYear(m[4]), tail: rest.slice(m[0].length) };
  }
  // October 4, Oct. 4th, Oct 4, 26
  if ((m = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b(?:,?\s+(\d{4}|\d{2})\b)?/i.exec(rest)) && monthOf(m[1])) {
    return { month: monthOf(m[1]), day: Number(m[2]), year: fullYear(m[3]), tail: rest.slice(m[0].length) };
  }
  // 4 October
  if ((m = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?\b(?:,?\s+(\d{4}|\d{2})\b)?/i.exec(rest)) && monthOf(m[2])) {
    return { month: monthOf(m[2]), day: Number(m[1]), year: fullYear(m[3]), tail: rest.slice(m[0].length) };
  }
  return null;
}

/** `month`/`day` as a date (in `year`, or the nearest year), within half a year of the report week; null when there is none. */
function dateNear(month: number, day: number, year: number | null, reportWeek: string): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const [ry, rm, rd] = reportWeek.split('-').map(Number);
  const anchor = dayNumber(ry, rm, rd);
  let best: { y: number; diff: number } | null = null;
  for (const y of year !== null ? [year] : [ry - 1, ry, ry + 1]) {
    const probe = new Date(Date.UTC(y, month - 1, day));
    if (probe.getUTCMonth() !== month - 1) continue; // February 30th and the like
    const diff = Math.abs(dayNumber(y, month, day) - anchor);
    if (!best || diff < best.diff) best = { y, diff };
  }
  if (!best || best.diff > 183) return null;
  return ymd(best.y, month, day);
}

/** The Sunday of the week that `month`/`day` (in `year`, or the nearest year) falls in, within half a year of the report week. */
function sundayNear(month: number, day: number, year: number | null, reportWeek: string): string | null {
  const date = dateNear(month, day, year, reportWeek);
  if (!date) return null;
  const [y, m, d] = date.split('-').map(Number);
  return addYmd(date, -new Date(Date.UTC(y, m - 1, d)).getUTCDay());
}

/**
 * What a look-ahead line is, if it starts with "Week of":
 *   • `week`: a label for a week it can place, as that week's Sunday;
 *   • `unplaced`: a label it can't place ("Week of TBD", or a date years away);
 *   • null: not a label, including a line that starts with one and goes on to
 *     say something ("Week of rain forecast: tarp the deck").
 */
type WeekLabel = { kind: 'week'; week: string } | { kind: 'unplaced' } | null;

function weekLabel(text: string, reportWeek: string): WeekLabel {
  const t = text.replace(/\s+/g, ' ').trim();
  const rest = WEEK_OF.exec(t)?.[1];
  if (rest === undefined || t.length > 160) return null;
  const date = leadingDate(rest);
  // No date: a short label it can't place ("Week of TBD", "Week of
  // Thanksgiving"), unless it has numbers, which make it a line ("Week of 3 trucks").
  if (!date) return t.length <= 40 && !/\d/.test(rest) ? { kind: 'unplaced' } : null;
  if (!LABEL_TAIL.test(date.tail)) return null;
  const week = sundayNear(date.month, date.day, date.year, reportWeek);
  return week ? { kind: 'week', week } : { kind: 'unplaced' };
}

/**
 * The week a "Week of …" label names, as its Sunday, resolved against the
 * upload's report week; null when the text isn't a label, or names no date
 * within half a year of it.
 *
 * Takes the forms a PM types besides the export's own "Week of October 11":
 * "Oct 11th", "10/11", "10-11-26", "2026-10-11", "11 October", a weekday
 * before the date, a year, a trailing range or note ("Week of October 11
 * (pour week)", "Week of Oct 11 - Oct 17"). A date that isn't a Sunday names
 * the week it falls in: the PM's own template labels weeks by their MONDAY.
 */
export function weekHeading(text: string, reportWeek: string): string | null {
  const label = weekLabel(text, reportWeek);
  return label?.kind === 'week' ? label.week : null;
}

/**
 * The day a progress-summary heading names ("Monday, September 28, 2026", as
 * the export writes them, or "9/28"), or null when it is anything else: a
 * heading that goes on to say something isn't a day.
 */
export function dayHeadingDate(text: string, reportWeek: string): string | null {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length > 60 || WEEK_OF.test(t)) return null;
  const date = leadingDate(t);
  if (!date || !/^\s*[:.]?\s*$/.test(date.tail)) return null;
  return dateNear(date.month, date.day, date.year, reportWeek);
}

/**
 * The Sunday of the week a Manpower "Week Of" cell names ("October 4
 * (scheduled)"), or null. By the date it STARTS with, whatever follows: in
 * that column the date is the label, so a PM's addition after it ("October 4
 * (forecast) - pour week", "October 4 subs") doesn't lose the week.
 */
function manpowerWeek(cell: string, reportWeek: string): string | null {
  const date = leadingDate(cell.replace(WEEK_OF, '$1').trim());
  return date ? sundayNear(date.month, date.day, date.year, reportWeek) : null;
}

/**
 * Cut to `max` UTF-16 units without splitting a surrogate pair: half an emoji
 * is not a character, and XML (so Word) has no way to hold one.
 */
const clip = (s: string, max: number) => {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
};
const field = (s: string | undefined): string | null => {
  const t = (s ?? '').trim();
  return t ? clip(t, CARRY_LIMITS.personField) : null;
};
/** Whitespace-insensitive, for comparing what the file says with what the Tracker wrote. */
const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').replace(/\s+/g, ' ').trim().toLowerCase() === (b ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
/** An empty cell, or the dash the export writes for "none". */
const blankCell = (s: string | undefined) => !s || /^[-–—\s]*$/.test(s);

interface TeamColumns { header: number; role: number; name: number; phone: number; email: number }

/**
 * Where a team table's columns are, from its header row: one of its first two
 * rows (a title row may sit above it), with Role and Name at least. Columns
 * are found by their header, so one inserted, removed or renamed ("Email
 * Address", "Phone Number") doesn't drop the table or shift its data.
 */
function teamColumns(rows: string[][]): TeamColumns | null {
  for (let h = 0; h < Math.min(2, rows.length); h++) {
    const keys = rows[h].map(headingKey);
    const role = keys.findIndex((k) => k === 'ROLE' || k === 'POSITION' || k === 'TITLE');
    // "Name" itself before "Full Name": a "Company Name" column must not win.
    const exact = keys.indexOf('NAME');
    const name = exact >= 0 ? exact : keys.findIndex((k) => k.endsWith('NAME') && !k.startsWith('COMPANY'));
    if (role < 0 || name < 0) continue;
    const phone = keys.findIndex((k) => k.startsWith('PHONE') || k.startsWith('CELL') || k.startsWith('MOBILE'));
    const email = keys.findIndex((k) => k.startsWith('EMAIL'));
    return { header: h, role, name, phone, email };
  }
  return null;
}

function personRows(rows: string[][], cols: TeamColumns): WeeklyReportCarryPerson[] {
  const at = (row: string[], i: number) => (i >= 0 ? row[i] : undefined);
  return rows.slice(cols.header + 1)
    .map((row) => ({ role: field(at(row, cols.role)) ?? '', name: field(at(row, cols.name)), phone: field(at(row, cols.phone)), email: field(at(row, cols.email)) }))
    // A header row repeated by hand is not a person.
    .filter((p) => !(headingKey(p.role) === 'ROLE' && headingKey(p.name ?? '').endsWith('NAME')))
    .filter((p) => p.role || p.name || p.phone || p.email)
    .slice(0, CARRY_LIMITS.people);
}

/** A table cut to the carry's limits; null when no cell holds anything. */
function carryTable(rows: string[][]): string[][] | null {
  const kept = rows.slice(0, CARRY_LIMITS.tableRows)
    .map((r) => r.slice(0, CARRY_LIMITS.tableColumns).map((c) => clip(c, CARRY_LIMITS.lineLength)));
  return kept.some((r) => r.some((c) => c.trim())) ? kept : null;
}

/**
 * Read a finalized report's .docx: the whole report as filed, and what was
 * typed in it (see the top of this file).
 *
 * @param reportWeek the Sunday of the week it is being uploaded to, for
 *   resolving "Week of October 11" headings to dates.
 * @param context what it was exported from; see ReadContext.
 * @throws NotAWordFileError when the file isn't a .docx at all.
 */
export async function readFinalizedReport(
  data: ArrayBuffer | Uint8Array,
  reportWeek: string,
  context: ReadContext = {},
): Promise<ReadReport> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(data);
  } catch {
    throw new NotAWordFileError();
  }
  const docPart = zip.file('word/document.xml');
  if (!docPart) throw new NotAWordFileError();
  const source = (await docPart.async('string')).split(W_STRICT).join(W);
  const xml = new DOMParser().parseFromString(source, 'application/xml');
  const body = xml.getElementsByTagNameNS(W, 'body')[0];
  if (!body || xml.getElementsByTagName('parsererror').length) throw new NotAWordFileError();

  const report = context.report;
  const jobTeam = context.team ?? report?.team ?? [];
  /**
   * Exported since the export marked its own lines (TRACKER_LINE_STYLE): its
   * styles define the mark, used or not. Then the mark alone says what the
   * export wrote. An older file is read by the older rules: equipment by item
   * name, any picture table in the progress summary as its photo grid.
   */
  const stylesPart = zip.file('word/styles.xml');
  const markedExport = !!stylesPart
    && new RegExp(`styleId="${TRACKER_LINE_STYLE}"`, 'i').test(await stylesPart.async('string'));

  const carry: WeeklyCarryInput = {
    customerTeam: [], team: [], jobTeam: [], details: [], lookahead: [], materials: [], survey: [], safety: [], logistics: [],
    tables: [], manpower: [], equipment: [], extraSections: [],
  };
  const lookahead = new Map<string, string[]>();
  const sectionsSeen = new Set<Section>();
  let section: Section | null = null;
  let sawEwTeam = false;
  let customerDone = false;
  let ewDone = false;
  let detailsDone = false;
  let manpowerDone = false;
  let lookWeek: string | null = null;
  let firstLookWeek: string | null = null;
  /** The last template section seen, where a section the PM added follows. */
  let lastKnown: Section | null = null;
  /** The PM's own section being read (an unknown styled heading, or text above the first heading). */
  // Typed by assertion, not inference: it is set inside the helpers below, and
  // a plain `= null` would let the compiler treat it as always null here.
  let extra = null as WeeklyReportExtraSection | null;

  // ── The whole report, as filed ──
  const filed: WeeklyReportFiled = { sections: [], truncated: false };
  let filedSection = null as WeeklyReportFiledSection | null;
  let budget: number = FILED_LIMITS.totalChars;
  /**
   * The text cap, or the section count, is reached: nothing after this point
   * is kept. ⚠️ Not the same as `filed.truncated`, which also marks a single
   * table or section cut short while the rest of the report still reads.
   */
  let full = false;
  /** The progress-summary day the blocks below belong to, for its photos. */
  let day: string | null = null;
  /** The last line read was a day's "Photos" heading: the table after it is that day's photo grid. */
  let photosNext = false;
  const openFiled = (title: string | null, key: Section | null) => {
    if (full) return;
    if (filed.sections.length >= FILED_LIMITS.sections) { filed.truncated = true; full = true; filedSection = null; return; }
    const t = title ? clip(title.replace(/\s+/g, ' ').trim(), FILED_LIMITS.titleLength) : null;
    if (t && t.length > budget) { filed.truncated = true; full = true; filedSection = null; return; }
    if (t) budget -= t.length;
    filedSection = { title: t, key, blocks: [] };
    filed.sections.push(filedSection);
  };
  const addFiled = (b: WeeklyReportFiledBlock) => {
    if (full) return;
    if (!filedSection) openFiled(null, null);
    const s = filedSection;
    if (!s) return;
    // A section past its block count loses its end; the next section still reads.
    if (s.blocks.length >= FILED_LIMITS.blocksPerSection) { filed.truncated = true; return; }
    const size = b.type === 'text' ? b.text.length : b.type === 'table' ? b.rows.flat().join('').length : 0;
    if (size > budget) { filed.truncated = true; full = true; return; }
    budget -= size;
    s.blocks.push(b);
  };
  // Anything cut short marks the report cut, so the tab says the rest is in
  // the Word file rather than showing less as if it were all.
  const filedText = (text: string, style: 'heading' | 'bullet' | 'line', date: string | null = null) => {
    if (text.length > FILED_LIMITS.textLength) filed.truncated = true;
    addFiled({ type: 'text', text: clip(text, FILED_LIMITS.textLength), style, date });
  };
  const filedTable = (rows: string[][], header: boolean) => {
    const kept = rows.slice(0, FILED_LIMITS.tableRows)
      .map((r) => r.slice(0, FILED_LIMITS.tableColumns).map((c) => clip(c, FILED_LIMITS.cellLength)));
    if (rows.length > FILED_LIMITS.tableRows
      || rows.some((r) => r.length > FILED_LIMITS.tableColumns && r.slice(FILED_LIMITS.tableColumns).some((c) => c.trim()))
      || rows.some((r) => r.some((c) => c.length > FILED_LIMITS.cellLength))) {
      filed.truncated = true;
    }
    if (kept.some((r) => r.some((c) => c.trim()))) addFiled({ type: 'table', rows: kept, header });
  };

  /** File one typed line where it belongs: the current look-ahead week, the current narrative section, Equipment, or the PM's own section. */
  const addLine = (line: string, style: 'heading' | 'bullet' | 'line' = 'line') => {
    if (!line) return;
    if (section === 'lookahead') {
      if (!lookWeek) return;
      const lines = lookahead.get(lookWeek) ?? [];
      if (lines.length < CARRY_LIMITS.linesPerSection) lines.push(clip(line, CARRY_LIMITS.lineLength));
      lookahead.set(lookWeek, lines);
      return;
    }
    if (section && NARRATIVE.includes(section)) {
      const list = carry[section as Narrative];
      if (list.length < CARRY_LIMITS.linesPerSection) list.push(clip(line, CARRY_LIMITS.lineLength));
      return;
    }
    if (section === 'equipment') {
      // Only a line that isn't one the Tracker wrote: those come back from the
      // job next week. Matched by the ITEM (isTrackerEquipmentLine), so an item
      // whose status or dates changed since the export isn't taken for typed.
      // In a marked export, an unmarked line is typed, whatever it starts with
      // (the marked ones never get here). An older file falls back to the item
      // name, and without the week's report carries nothing.
      const ours = markedExport ? false : !report?.equipment || isTrackerEquipmentLine(line, report.equipment);
      if (!ours && carry.equipment.length < CARRY_LIMITS.linesPerSection) {
        carry.equipment.push(clip(line, CARRY_LIMITS.lineLength));
      }
      return;
    }
    underTable();
    if (extra && extra.blocks.length < CARRY_LIMITS.linesPerSection) {
      extra.blocks.push({ type: 'text', text: clip(line, CARRY_LIMITS.lineLength), style, date: null });
    }
  };

  /**
   * In a template section that is only its table (Project Details, the
   * teams, Manpower, and Equipment for a table), what the PM types under it
   * has no field of its own: it carries untitled, right after the section, as
   * the export then writes it, so it reads back the same way next week.
   */
  const underTable = (isTable = false) => {
    // The look-ahead's lines carry by week; a TABLE in it has no week, and
    // carries after it the same way. (A line can't: written after the weeks,
    // it would read back as the last week's.)
    const here = !!section && (TABLE_SECTIONS.includes(section) || (isTable && section === 'lookahead'));
    if (extra || !here) return;
    if (carry.extraSections.length >= CARRY_LIMITS.extraSections) return;
    extra = { title: '', after: section, blocks: [] };
    carry.extraSections.push(extra);
  };

  /** Any heading seen yet, known or not: above the first one is the PM's own preamble. */
  let sawHeading = false;
  /** Text and tables above the first heading: carried to the top of next week's report. */
  const preamble = () => { if (!sawHeading && extra === null) openExtra(''); };

  /** Start a section of the PM's own, carried whole after the template section before it. */
  const openExtra = (title: string) => {
    if (carry.extraSections.length >= CARRY_LIMITS.extraSections) { extra = null; return; }
    extra = { title: clip(title.replace(/\s+/g, ' ').trim(), FILED_LIMITS.titleLength), after: lastKnown, blocks: [] };
    carry.extraSections.push(extra);
  };

  // Each paragraph's lines (soft breaks, Shift+Enter, split them), trimmed
  // line by line: no regex runs across a whole paragraph of spaces.
  const blocks = bodyBlocks(body).map((b) => (b.kind === 'p'
    ? { ...b, lines: b.text.replace(NBSP, ' ').split('\n').map((l) => l.trim()).filter(Boolean) }
    : b));

  /**
   * The customer team's heading as the export writes it ("Customer Co Team",
   * "Customer Team" with no client), when the week's report is known.
   */
  const customerKey = report ? headingKey(`${report.project.client?.trim() || 'Customer'} Team`) : null;
  /**
   * The section a heading's text names, or null. Before the E&W team, the
   * export's customer heading, or a generic one ("Owner Team"), is the
   * customer's. ⚠️ Not any "… Team": a section the PM adds above it
   * ("Subcontractor Team") would be taken for it, and its text never carried.
   * A customer heading renamed past these still works: the team table under
   * it is the customer team whatever the heading says (see the table rule).
   */
  const sectionNamed = (text: string): Section | null => {
    if (!text || text.length > 80) return null;
    const key = headingKey(text);
    const customer = key.endsWith('TEAM') && (customerKey === null || key === customerKey || /^(CUSTOMER|CLIENT|OWNER)S?TEAM$/.test(key));
    return HEADINGS[key] ?? (!sawEwTeam && !customerDone && customer ? 'customerTeam' : null);
  };
  /**
   * ⚠️ When the file still has its headings STYLED as headings, a plain
   * paragraph is a heading only if it is ALL BOLD capitals, which is what the
   * export's headings stay as when their style is cleared to Normal; a pasted
   * plain "SURVEY" line is a line. A file whose styles are all gone (pasted
   * into a blank document, say) falls back to any plain capitals.
   */
  const styledHeadings = blocks.some((b) => b.kind === 'p' && b.level >= 1 && b.level <= 2
    && b.lines.length > 0 && (!!HEADINGS[headingKey(b.lines.join(' '))] || !!HEADINGS[headingKey(b.lines[0])]));

  const openWeek = (week: string) => {
    lookWeek = week;
    firstLookWeek ??= week;
  };
  /**
   * In the look-ahead: file a paragraph's lines around any week labels among
   * them (one paragraph can hold several weeks, split by soft breaks); false
   * when none is a label. A bullet opens a week only when it is a label it can
   * place, and never ends one: an outline of weeks in bullets still reads, and
   * a bullet that merely starts "Week of …" stays a line.
   */
  const fileWeekLines = (lines: string[], list: boolean): boolean => {
    // "Week of" + break + "October 11": the whole paragraph is the label.
    if (lines.length > 1 && weekLabel(lines[0], reportWeek)?.kind !== 'week') {
      const whole = weekLabel(lines.join(' '), reportWeek);
      if (whole?.kind === 'week') { openWeek(whole.week); return true; }
    }
    const labels = lines.map((l) => weekLabel(l, reportWeek));
    const isLabel = (l: WeekLabel) => l?.kind === 'week' || (l?.kind === 'unplaced' && !list);
    if (!labels.some(isLabel)) return false;
    let buffer: string[] = [];
    const flush = () => { if (buffer.length) addLine(buffer.join('\n')); buffer = []; };
    lines.forEach((line, i) => {
      const l = labels[i];
      if (!isLabel(l)) { buffer.push(line); return; }
      flush();
      // A week it can't place ("Week of TBD") ends the week above it, rather
      // than filing its own text and what follows under the wrong week.
      if (l?.kind === 'week') openWeek(l.week); else lookWeek = null;
    });
    flush();
    return true;
  };

  /**
   * A look-ahead paragraph as filed: its week labels as sub-headings and its
   * other lines as typed, so a label and a line split only by a soft break
   * don't read as one heading. "Week of" + break + "October 11" is one label.
   */
  const fileLookahead = (lines: string[], list: boolean) => {
    const whole = weekLabel(lines.join(' '), reportWeek);
    if (lines.length > 1 && whole?.kind === 'week' && weekLabel(lines[0], reportWeek)?.kind !== 'week') {
      filedText(lines.join(' '), 'heading');
      return;
    }
    for (const line of lines) {
      const l = weekLabel(line, reportWeek);
      const label = l?.kind === 'week' || (l?.kind === 'unplaced' && !list);
      filedText(line, label ? 'heading' : list ? 'bullet' : 'line');
    }
  };

  for (const b of blocks) {
    if (b.kind === 'tbl') {
      // A photo table: the tab shows the Tracker's own photos for that day here.
      // The export's photo grid comes right under a day's "Photos" heading, and
      // holds no picture at all when none of them could be loaded for it. ⚠️
      // Only in the progress summary: a picture pasted into a table anywhere
      // else drops out like any pasted image, and the table's text is kept
      // (Codex on #640).
      // In a marked export, it is the export's grid when it has the export's own
      // text in it, or sits right under a day's "Photos" heading, or has no text
      // at all; anything the PM typed into it is kept, under the photos. A table
      // the PM made there, with a picture and only their own text, keeps its text.
      const photoLike = section === 'progress' && (b.photos || photosNext);
      const photoGrid = photoLike && (!markedExport || photosNext || b.lines.own > 0 || b.lines.others.length === 0);
      photosNext = false;
      if (photoGrid) {
        addFiled({ type: 'photos', date: day });
        if (markedExport) for (const text of b.lines.others) filedText(text, 'line');
        continue;
      }
      filedTable(b.rows, b.header);
      const cols = teamColumns(b.rows);
      // The FIRST team table under each heading: one the PM adds below it
      // (subcontractors, say) must not replace it, and carries as typed (underTable).
      if (cols && section === 'ewTeam' && !ewDone) {
        teamTable(personRows(b.rows, cols));
        ewDone = true;
        continue;
      }
      // The customer team is the team table before the E&W one, under its own heading or none.
      if (cols && !sawEwTeam && !customerDone) {
        carry.customerTeam = personRows(b.rows, cols);
        customerDone = true;
        // Whatever its heading said, this is the customer team's section: what
        // the PM types under it carries after it (underTable), and a section
        // they add next follows it.
        section = 'customerTeam';
        lastKnown = 'customerTeam';
        // Under a heading it didn't know ("OWNER CONTACTS"), that heading was
        // this table's: not a section of the PM's own to carry empty.
        if (extra && extra.title && extra.blocks.length === 0) {
          carry.extraSections.splice(carry.extraSections.indexOf(extra), 1);
          extra = null;
          if (filedSection) filedSection.key = 'customerTeam';
        }
        continue;
      }
      if (section === 'details' && !detailsDone) { detailsTable(b.rows, b.header); detailsDone = true; continue; }
      if (section === 'manpower' && !manpowerDone) { manpowerTable(b.rows); manpowerDone = true; continue; }
      const rows = carryTable(b.rows);
      if (!rows) continue;
      if (section === null) preamble();
      if (section && NARRATIVE.includes(section)) {
        if (carry.tables.length < CARRY_LIMITS.tables) carry.tables.push({ section: section as Narrative, rows, header: b.header });
        continue;
      }
      underTable(true);
      if (extra && extra.blocks.length < CARRY_LIMITS.linesPerSection) {
        extra.blocks.push({ type: 'table', rows, header: b.header });
      }
      continue;
    }
    const { lines } = b;
    if (!lines.length) continue;
    const whole = lines.join(' ');

    // A heading: styled as a top-level one (Heading 1-2, Title), or plain
    // capitals as above; never a bullet, never a sub-heading. The whole
    // paragraph names the section ("THREE-WEEK" + break + "LOOK AHEAD"), or a
    // styled heading's FIRST line does, with typed text after a soft break.
    const topHeading = b.level >= 1 && b.level <= 2;
    const capitals = !b.list && b.level === 0 && /[A-Z]/.test(whole) && whole === whole.toUpperCase()
      && (!styledHeadings || b.bold);
    if (topHeading || capitals) {
      const named = sectionNamed(whole);
      const first = !named && topHeading && lines.length > 1 ? sectionNamed(lines[0]) : null;
      if (named || first) {
        sawHeading = true;
        section = (named ?? first)!;
        sectionsSeen.add(section);
        lastKnown = section;
        extra = null;
        day = null;
        if (section === 'ewTeam') sawEwTeam = true;
        lookWeek = null;
        openFiled(first ? lines[0] : whole, section);
        const tail = first ? lines.slice(1) : [];
        if (tail.length) {
          if (section === 'lookahead') fileLookahead(tail, b.list); else filedText(tail.join('\n'), 'line');
          if (!(section === 'lookahead' && fileWeekLines(tail, b.list))) addLine(tail.join('\n'));
        }
        continue;
      }
    }

    // How the line reads as filed: a bullet, a sub-heading (Heading 3 and
    // below, or a bold line, as the export writes "Week of", "Crews · …" and
    // each day), or a plain line.
    const style = b.list ? 'bullet' : b.level >= 3 || (b.level === 0 && b.bold) ? 'heading' : 'line';

    // Before the unknown-heading rule below, so a "Week of" line the PM
    // restyled as a heading still opens (or ends) its week.
    if (section === 'lookahead' && fileWeekLines(lines, b.list)) {
      fileLookahead(lines, b.list);
      continue;
    }

    // A top-level heading this doesn't know (one the PM added, or renamed)
    // starts a section of the PM's own, so what follows isn't filed under the
    // template section above it.
    if (topHeading) {
      sawHeading = true;
      section = null;
      lookWeek = null;
      day = null;
      openFiled(whole, null);
      openExtra(whole);
      continue;
    }

    if (section === null) preamble();

    if (section === 'progress' && style === 'heading') {
      const date = dayHeadingDate(whole, reportWeek);
      if (date || WEEK_OF.test(whole)) day = date;
      photosNext = /^photos$/i.test(whole);
      filedText(lines.join('\n'), style, date);
      continue;
    }
    photosNext = false;
    filedText(lines.join('\n'), style);
    // The export's own lines, by their mark: never typed, even an equipment
    // item that has left the job's list since (which name-matching alone can't
    // tell). The Manpower note by its text too, unmarked in an older file.
    if (b.tracker || (section === 'manpower' && same(whole, MANPOWER_FOOTNOTE))) continue;
    addLine(lines.join('\n'), style);
  }

  /**
   * The E&W team table: rows for roles the job doesn't fill carry whole; rows
   * for its own roles carry only a phone or email TYPED for the same person
   * (different from the job's, so the job's own never comes back as typed).
   */
  function teamTable(rows: WeeklyReportCarryPerson[]) {
    const jobRoles = jobRoleKeys(jobTeam);
    carry.team = rows.filter((row) => !isJobRole(row.role, jobRoles));
    carry.jobTeam = rows
      .filter((row) => isJobRole(row.role, jobRoles) && row.name)
      .map((row) => {
        const ours = jobTeam.find((t) => t.name && same(t.name, row.name));
        return {
          role: row.role,
          name: row.name,
          phone: row.phone && !same(row.phone, ours?.phone) ? row.phone : null,
          email: row.email && !same(row.email, ours?.email) ? row.email : null,
        };
      })
      .filter((row) => row.phone || row.email);
  }

  /** Project Details: values that differ from what the Tracker wrote, and rows the PM added. */
  function detailsTable(rows: string[][], header: boolean) {
    if (!report) return;
    const ours = trackerDetails(report.project);
    const firstIsHeader = header || (rows[0] && headingKey(rows[0][0] ?? '') === 'FIELD');
    const parsed = rows.slice(firstIsHeader ? 1 : 0)
      .map((r) => ({ label: field(r[0]) ?? '', value: clip(r.slice(1).map((c) => c.trim()).filter(Boolean).join(' '), CARRY_LIMITS.lineLength) }))
      .filter((d) => d.label && d.value);
    // Which row is the template's for each of its labels: the one still holding
    // the Tracker's value, or failing that the first (its value edited). Every
    // other row with that label (a second "Project Address") is one the PM
    // added, above or below it, and is carried whole (Codex on #640, Manpower).
    const own = new Set<(typeof parsed)[number]>();
    for (const o of ours) {
      const rowsFor = parsed.filter((d) => labelKey(d.label) === labelKey(o.label));
      const mine = rowsFor.find((d) => same(d.value, o.value)) ?? rowsFor[0];
      if (mine) own.add(mine);
    }
    carry.details = parsed
      .map((d) => ({ ...d, added: ours.some((o) => labelKey(o.label) === labelKey(d.label)) && !own.has(d) }))
      .filter((d) => d.added || !same(ours.find((o) => labelKey(o.label) === labelKey(d.label))?.value, d.value))
      .slice(0, CARRY_LIMITS.rows);
  }

  /**
   * Manpower: rows by week, with what was typed in them. A count or note the
   * Tracker wrote isn't typed; a row it didn't write (a later week, a
   * subcontractor line) is, whole.
   */
  function manpowerTable(rows: string[][]) {
    const keys = (rows[0] ?? []).map(headingKey);
    const col = (test: (k: string) => boolean, fallback: number) => {
      const i = keys.findIndex(test);
      return i >= 0 ? i : fallback;
    };
    const hasHeader = keys.some((k) => k.startsWith('WEEK') || k.includes('COUNT'));
    const week = col((k) => k.startsWith('WEEK'), 0);
    const count = col((k) => k.includes('COUNT') || k.startsWith('CREW'), 1);
    const notes = col((k) => /^(NOTE|SOURCE|COMMENT|REMARK)/.test(k), 2);
    if (!report?.manpower) return;
    const live = report.manpower.rows;
    const parsed = rows.slice(hasHeader ? 1 : 0)
      .map((r) => {
        const label = field(r[week]) ?? '';
        return { label, weekStart: label ? manpowerWeek(label, reportWeek) : null, count: r[count], note: r[notes] };
      })
      .filter((p) => p.label);
    /**
     * Which row is the Tracker's own for each of its weeks: the one whose label
     * is still the export's ("October 4 (forecast)"), or failing that the first
     * row with that date (its label edited). Every other row is one the PM
     * ADDED, a subcontractor breakout say, and carries whole even when its date
     * is one the Tracker has a row for (Codex on #640).
     */
    const tracker = new Map<(typeof parsed)[number], InternalWeeklyReport['manpower']['rows'][number]>();
    for (const row of live) {
      const own = parsed.find((p) => p.weekStart === row.weekStart && same(p.label, manpowerLabel(row)))
        ?? parsed.find((p) => p.weekStart === row.weekStart && !tracker.has(p));
      if (own) tracker.set(own, row);
    }
    for (const p of parsed) {
      const ours = tracker.get(p);
      const typedCount = blankCell(p.count) ? null : field(p.count);
      const typedNote = field(p.note);
      const ourNote = ours ? manpowerNote(report as InternalWeeklyReport, ours) : null;
      const m = ours
        ? {
          weekStart: p.weekStart,
          label: p.label,
          headcount: typedCount && !same(typedCount, ours.headcount == null ? '' : String(ours.headcount)) ? typedCount : null,
          notes: typedNote && !same(typedNote, ourNote) ? clip(typedNote, CARRY_LIMITS.lineLength) : null,
          added: false,
        }
        : { weekStart: p.weekStart, label: p.label, headcount: typedCount, notes: typedNote ? clip(typedNote, CARRY_LIMITS.lineLength) : null, added: true };
      if ((m.headcount || m.notes) && carry.manpower.length < CARRY_LIMITS.rows) carry.manpower.push(m);
    }
  }

  carry.lookahead = [...lookahead.entries()]
    .filter(([, lines]) => lines.length > 0)
    .map(([weekStart, lines]) => ({ weekStart, lines }))
    .slice(0, CARRY_LIMITS.lookaheadWeeks);
  // A heading alone, with nothing under it and no title (text above the first heading that was all blank), carries nothing.
  carry.extraSections = carry.extraSections.filter((s) => s.title || s.blocks.length);

  return { kind: await footerKind(zip, sectionsSeen), weekStart: firstLookWeek, carry, filed };
}

/**
 * Internal or client, from the footer the export writes ("Confidential –
 * Internal Use Only" vs "Confidential"). Only a file whose footer says
 * neither falls back to the sections only the internal copy has: the footer
 * is the export's own statement, a section is a guess from the text.
 */
async function footerKind(zip: JSZip, sections: ReadonlySet<Section>): Promise<'internal' | 'client' | null> {
  const footers = Object.keys(zip.files).filter((f) => /^word\/footer\d*\.xml$/.test(f));
  // Parsed, not stripped with a tag regex: that runs in quadratic time on a
  // footer full of "<" with no ">".
  const text = (await Promise.all(footers.map((f) => zip.file(f)!.async('string'))))
    .map((xml) => new DOMParser().parseFromString(xml, 'application/xml').documentElement?.textContent ?? '')
    .join(' ').toLowerCase();
  if (/internal\s+use\s+only/.test(text)) return 'internal';
  if (text.includes('confidential')) return 'client';
  if (sections.has('manpower') || sections.has('equipment')) return 'internal';
  return null;
}

/** A template section as the upload window names it: "Text under Manpower". */
const UNDER: Record<Section, string> = {
  details: 'Project Details', customerTeam: 'the customer team', ewTeam: 'the East & Westbrook team', progress: 'the progress summary',
  lookahead: 'the look-ahead', materials: 'Materials', manpower: 'Manpower', survey: 'Survey', safety: 'Safety', equipment: 'Equipment',
  logistics: 'Logistics',
};

/** How much of each section was read, for the upload window: "Materials: 2 lines". */
export function carrySummary(carry: WeeklyCarryInput): string[] {
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  const out: string[] = [];
  const customer = carry.customerTeam.filter((p) => p.name).length;
  if (customer) out.push(`Customer team: ${n(customer, 'person', 'people')}`);
  const team = carry.team.filter((p) => p.name).length;
  if (team) out.push(`East & Westbrook team: ${n(team, 'person', 'people')}`);
  if (carry.jobTeam.length) out.push(`Phone or email typed for ${n(carry.jobTeam.length, 'person', 'people')} on the job`);
  if (carry.details.length) out.push(`Project details: ${n(carry.details.length, 'field', 'fields')}`);
  if (carry.lookahead.length) out.push(`Look-ahead: ${n(carry.lookahead.length, 'week', 'weeks')}`);
  for (const [label, lines] of [['Materials', carry.materials], ['Survey', carry.survey], ['Safety', carry.safety], ['Logistics', carry.logistics]] as const) {
    if (lines.length) out.push(`${label}: ${n(lines.length, 'line', 'lines')}`);
  }
  if (carry.tables.length) out.push(`Tables: ${carry.tables.length}`);
  if (carry.manpower.length) out.push(`Manpower: ${n(carry.manpower.length, 'row', 'rows')} with notes or counts`);
  if (carry.equipment.length) out.push(`Equipment: ${n(carry.equipment.length, 'line', 'lines')}`);
  for (const s of carry.extraSections) {
    // Said as what it is: a pasted schedule under the look-ahead is a table, not text.
    const tables = s.blocks.length > 0 && s.blocks.every((b) => b.type === 'table');
    const what = !tables ? 'Text' : s.blocks.length === 1 ? 'A table' : 'Tables';
    out.push(s.title ? `Your section “${s.title}”` : s.after ? `${what} under ${UNDER[s.after]}` : `${what} above Project Details`);
  }
  return out;
}

/** How much of the report was read, for the upload window: "11 sections". */
export function filedSummary(filed: WeeklyReportFiled): string {
  const k = filed.sections.filter((s) => s.title).length;
  return `${k} ${k === 1 ? 'section' : 'sections'}${filed.truncated ? ' (the end was cut: the file is longer than the Tracker keeps)' : ''}`;
}
